// src/app/components/OrgSwitcher/OrgSwitcher.jsx
// Merged user + org trigger. Modal for switching. User name/role visible in trigger.

import React, { useState, useEffect, useRef } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, Home, Briefcase, X, ChevronRight, Plus, LogOut, Settings, ChevronDown } from 'lucide-react';
import * as authActions from '../../modules/Auth/_redux/authRedux';
import './org-switcher.scss';

const ORG_ICONS = { residential_estate: Home, residential_building: Home, office_building: Building2, commercial_building: Building2 };
const getIcon   = (type) => ORG_ICONS[type] || Briefcase;
const fmtType   = (type) => (type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const trialDays = (d) => { if (!d) return null; return Math.max(0, Math.ceil((new Date(d) - new Date()) / 86400000)); };

const SUB = {
  active:    { label: 'Active',    color: '#16A34A' },
  trial:     { label: 'Trial',     color: '#2563EB' },
  past_due:  { label: 'Past Due',  color: '#DC2626' },
  suspended: { label: 'Suspended', color: '#D97706' },
  cancelled: { label: 'Cancelled', color: '#6B7280' },
};

export function OrgSwitcher({ onLogout }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const overlayRef = useRef(null);

  const { org, orgs, user } = useSelector(s => ({
    org:  s.auth.organization,
    orgs: s.auth.organizations || [],
    user: s.auth.user,
  }), shallowEqual);

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  const handleSwitch = async (target) => {
    // alert('jhj');
    if (switching) return;
    setSwitching(true);
    try {
      dispatch(authActions.actions.fulfillOrg(target));
      dispatch(authActions.actions.requestSwitchOrg(target.organization_id));
      setOpen(false);
      setTimeout(() => window.location.reload(), 300);
    } catch {
      setSwitching(false);
    }
  };

  if (!org || !user) return null;

  const userName  = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'User';
  const userInit  = `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() || 'U';
  const subStatus = org.subscription?.status;
  const subCfg    = SUB[subStatus];
  const OrgIcon   = getIcon(org.type);

  // Find current user's role in current org
  const myMembership = orgs.find(o => o.organization_id === org.organization_id);
  const myRole = myMembership?.membership?.role || org.membership?.role || user.role || '';

  return (
    <>
      {/* ── Trigger (replaces the old separate user card) ── */}
      <button className="osw-trigger" onClick={() => setOpen(true)}>
        {/* User avatar */}
        <div className="osw-avatar">{userInit}</div>

        {/* User + org info */}
        <div className="osw-trigger__text">
          <div className="osw-trigger__name">{userName}</div>
          <div className="osw-trigger__org">
            <OrgIcon size={11} style={{ flexShrink: 0 }} />
            <span>{org.name}</span>
            {subCfg && subStatus !== 'active' && (
              <span style={{ color: subCfg.color, fontWeight: 700, fontSize: 10 }}>
                · {subStatus === 'trial' ? `Trial ${trialDays(org.subscription?.trial_ends_at) ?? ''}d` : subCfg.label}
              </span>
            )}
          </div>
        </div>

        <ChevronDown size={13} color="#9CA3AF" style={{ flexShrink: 0 }} />
      </button>

      {/* ── Modal ── */}
      {open && (
        <div
          className="osw-overlay"
          ref={overlayRef}
          onClick={e => { if (e.target === overlayRef.current) setOpen(false); }}
        >
          <div className="osw-modal">
            {/* Header with user info */}
            <div className="osw-modal__head">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="osw-avatar osw-avatar--lg">{userInit}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#1C1C2E' }}>{userName}</div>
                  <div style={{ fontSize: 12, color: '#7E8299', marginTop: 1 }}>{user.email}</div>
                  {myRole && (
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#5341C4', background: '#F0EEFF', padding: '1px 7px', borderRadius: 4, display: 'inline-block', marginTop: 3, textTransform: 'capitalize' }}>{myRole}</div>
                  )}
                </div>
              </div>
              <button className="osw-close" onClick={() => setOpen(false)}><X size={14} /></button>
            </div>

            {/* Org list */}
            <div className="osw-modal__section-label">Organizations</div>
            <div className="osw-modal__list">
              {orgs.length === 0 ? (
                <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 13, color: '#9CA3AF' }}>No organizations found.</div>
              ) : orgs.map(o => {
                const Icon   = getIcon(o.type);
                const active = org.organization_id === o.organization_id;
                const sub    = o.subscription?.status;
                const sc     = SUB[sub];
                const days   = sub === 'trial' ? trialDays(o.subscription?.trial_ends_at || o.subscription?.expires_at) : null;
                const role   = o.membership?.role;

                return (
                  <button
                    key={o.organization_id}
                    className={`osw-org-row ${active ? 'osw-org-row--active' : ''}`}
                    onClick={() => !active && handleSwitch(o)}
                    disabled={active || switching}
                  >
                    <div className="osw-org-row__icon" style={active ? { background: '#5341C4', color: '#fff' } : {}}>
                      <Icon size={15} />
                    </div>
                    <div className="osw-org-row__info">
                      <div className="osw-org-row__name-line">
                        <span className="osw-org-row__name">{o.name}</span>
                        {role && <span className="osw-org-row__role" style={active ? { background: '#5341C4', color: '#fff' } : {}}>{role.charAt(0).toUpperCase() + role.slice(1)}</span>}
                      </div>
                      <div className="osw-org-row__meta">
                        <span>{fmtType(o.type)}</span>
                        {sc && (
                          <>
                            <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#DDDDE8', flexShrink: 0, display: 'inline-block' }} />
                            <span style={{ color: sc.color, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <span style={{ width: 5, height: 5, borderRadius: '50%', background: sc.color, display: 'inline-block' }} />
                              {sub === 'trial' && days != null ? `Trial · ${days}d` : sc.label}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      {active
                        ? <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#F0FDF4', color: '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={12} /></div>
                        : <ChevronRight size={13} color="#DDDDE8" />
                      }
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="osw-modal__footer">
              {/* <button className="osw-add-btn" onClick={() => { setOpen(false); navigate('/onboarding/new-organization'); }}>
                <Plus size={12} /> Add Organization
              </button> */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="osw-footer-btn" onClick={() => { setOpen(false); navigate('/settings'); }}>
                  <Settings size={13} /> Settings
                </button>
                <button className="osw-footer-btn osw-footer-btn--danger" onClick={() => { setOpen(false); navigate('/logout'); }}>
                  <LogOut size={13} /> Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}